import { TOOLS } from './../constants/edit';
import { useState } from 'react';
import { isEqual, last } from 'lodash';
import { IDrawingContext } from '../contexts/drawingContext';
import { IWordsImage } from '../contexts/wordsPageContext';
import { INote, ReplacedImage, EmergentExemplar, LaterExemplar, ILabel, SavedLabel, ExemplarWord } from '../types';

interface IUndoRedo {
  events: Event[];
  undoneEvents: Event[];
  isEventsNotEmpty: boolean;
  isUndoneEventsNotEmpty: boolean;
  saveEvent: (type: Type, prevState: State, state: State, creator: EventCreator) => void;
  undoEvent: () => Event | undefined;
  redoEvent: () => Event | undefined;
  clearEvents: () => void;
}

export enum Type {
  pen = 'pen',
  hideDrawings = 'hideDrawings',
  highlight = 'highlight',
  hideHighlights = 'hideHighlights',
  replaceImage = 'replaceImage',
  hideImage = 'hideImage',
  addNote = 'addNote',
  removeNote = 'removeNote',
  moveNote = 'moveNote',
  updateNoteContent = 'updateNoteContent',
  hideNotes = 'hideNotes',
  updateExemplarText = 'updateExemplarText',
  hideText = 'hideText',
  setLabels = 'setLabels',
  hideVisibleLabel = 'hideVisibleLabel',
  showHiddenLabel = 'showHiddenLabel',
  hideLabels = 'hideLabels',
  setLanguage = 'setLanguage',
}

export type UndoRedoAction =
  | IDrawingContext['lines']
  | ExemplarWord
  | IWordsImage
  | ReplacedImage
  | boolean
  | Array<string | number>
  | INote[]
  | LaterExemplar
  | EmergentExemplar
  | Array<ILabel | SavedLabel>;

export type State = UndoRedoAction;

export type EventCreator = (state: State) => void;

export type Event = {
  type: Type;
  prevState: State;
  state: State;
  creator: EventCreator;
};

export const useUndoRedo = (): IUndoRedo => {
  const [events, setEvents] = useState<Event[]>([]);
  const [undoneEvents, setUndoneEvents] = useState<Event[]>([]);

  const isEventsNotEmpty = events.length > 0;
  const isUndoneEventsNotEmpty = undoneEvents.length > 0;

  const saveEvent = (type: Type, prevState: State, state: State, creator: EventCreator): void => {
    const hideTools = [
      TOOLS.hideNotes.name,
      TOOLS.hideDrawings.name,
      TOOLS.hideHighlights.name,
      TOOLS.hideText.name,
      Type.hideImage,
      Type.hideVisibleLabel,
      Type.showHiddenLabel,
      Type.hideLabels,
    ];
    const newEvent = {
      type,
      prevState,
      state,
      creator,
    };

    const lastSavedEvent = events[events.length - 1];
    if (isEqual(newEvent, lastSavedEvent)) {
      return;
    }

    /** When new events saves, undone stack should be cleared */
    setUndoneEvents([]);

    setEvents(prevEvents => {
      const isLastEventEqualToNewEvent = getLastEvent(prevEvents)?.type === newEvent.type;
      const isHideToolEvent = hideTools.includes(newEvent.type);
      if (isLastEventEqualToNewEvent && isHideToolEvent) {
        return setHideEvents(prevEvents);
      }

      if (newEvent.type === Type.showHiddenLabel) {
        return prevEvents.slice(0, -1);
      }

      return [...prevEvents, newEvent];
    });

    const setHideEvents = (prevEvents: Event[]): Event[] => {
      const lastEvent = getLastEvent(prevEvents);
      const hideUniqueElementsTools = [Type.hideImage, Type.hideVisibleLabel];

      return lastEvent && hideUniqueElementsTools.includes(newEvent.type)
        ? setHideUniqueElementEvent(lastEvent, prevEvents)
        : prevEvents.slice(0, -1);
    };

    const setHideUniqueElementEvent = (lastEvent: Event, prevEvents: Event[]): Event[] => {
      let isHidingNewElement = lastEvent.state != newEvent.state;

      if (newEvent.type === Type.hideImage) {
        const prevState = lastEvent.prevState as Array<string | number>;
        const state = newEvent.state as Array<string | number>;

        isHidingNewElement = isHidingNewElement && !isEqual(prevState, state);
      }

      return isHidingNewElement ? [...prevEvents, newEvent] : prevEvents.slice(0, -1);
    };
  };

  const getLastEvent = (prevEvents: Event[]): Event | undefined => {
    return last(prevEvents);
  };

  const undoEvent = (): Event | undefined => {
    const lastEvent = events.pop();
    /** Event should not be fully removed, we need it for the redo */
    if (lastEvent) {
      setUndoneEvents(prevEvents => [...prevEvents, lastEvent]);
    }

    return lastEvent;
  };

  const redoEvent = (): Event | undefined => {
    const lastUndoneEvent = undoneEvents.pop();
    /** Push the last undone event from undoneEvents stack to all events */
    if (lastUndoneEvent) {
      setEvents(prevEvents => [...prevEvents, lastUndoneEvent]);
    }

    return lastUndoneEvent;
  };

  const clearEvents = (): void => {
    setEvents([]);
    setUndoneEvents([]);
  };

  return {
    events,
    undoneEvents,
    isEventsNotEmpty,
    isUndoneEventsNotEmpty,
    saveEvent,
    undoEvent,
    redoEvent,
    clearEvents,
  };
};
